import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { api, type DocAudience, type DocFile, type DocLanguage, type DocLink } from "../api";
import { relative } from "../components/ui";
import { FileVideo, LinkEmbed, OpenFile, bytes } from "./project/Docs";

// The question: where is every project's user guide, and which videos show how to use them?

interface AllDocs {
  guides: { project: string; projectName: string; slug: string; title: string; language: DocLanguage; audience: DocAudience; updatedAt: string; updatedBy: string | null }[];
  links: (DocLink & { project: string; projectName: string })[];
  files: (DocFile & { project: string; projectName: string })[];
}

const short = (name: string) => name.split(" — ")[0];

export function DocsPage() {
  const q = useQuery({ queryKey: ["all-docs"], queryFn: () => api<AllDocs>("/docs") });
  const [filter, setFilter] = useState("");
  const [play, setPlay] = useState<string | null>(null);

  const byProject = useMemo(() => {
    const n = filter.trim().toLowerCase();
    const hit = (...s: string[]) => !n || s.some((x) => x.toLowerCase().includes(n));
    const map = new Map<string, { name: string; guides: AllDocs["guides"]; links: AllDocs["links"]; files: AllDocs["files"] }>();
    const get = (slug: string, name: string) => {
      let e = map.get(slug);
      if (!e) map.set(slug, (e = { name, guides: [], links: [], files: [] }));
      return e;
    };
    for (const g of q.data?.guides ?? []) if (hit(g.title, g.projectName, g.project)) get(g.project, g.projectName).guides.push(g);
    for (const l of q.data?.links ?? []) if (hit(l.title, l.projectName, l.project, l.notes)) get(l.project, l.projectName).links.push(l);
    for (const f of q.data?.files ?? []) if (hit(f.title, f.projectName, f.project, f.notes)) get(f.project, f.projectName).files.push(f);
    return [...map.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name));
  }, [q.data, filter]);

  const videos = (q.data?.links.filter((l) => l.kind === "video").length ?? 0) + (q.data?.files.filter((f) => f.kind === "video").length ?? 0);

  return (
    <div className="canvas">
      <div className="title-row">
        <div>
          <h1>Docs</h1>
          <p className="sub">
            {q.data ? `${q.data.guides.length} user guide${q.data.guides.length === 1 ? "" : "s"} and ${videos} video${videos === 1 ? "" : "s"} across ${((n) => `${n} project${n === 1 ? "" : "s"}`)(new Set([...q.data.guides, ...q.data.links, ...q.data.files].map((x) => x.project)).size)}. Each project's wiki is on its Docs tab.` : "Every project's user guides and videos."}
          </p>
        </div>
        <input className="search-field" type="search" aria-label="Filter docs" placeholder="Filter by project or title" value={filter} onChange={(e) => setFilter(e.target.value)} />
      </div>
      {q.error && <p className="error">{q.error.message}</p>}
      {q.isPending && <span className="skeleton" style={{ width: 320, height: 16 }} />}
      {q.data && byProject.length === 0 && <div className="empty">{filter ? "Nothing matches." : "No project has a user guide or a video yet. Add them on a project's Docs tab."}</div>}
      {byProject.map(([slug, p]) => (
        <section key={slug} className="panel flush" aria-labelledby={`docs-${slug}`}>
          <div className="panel-head">
            <h2 id={`docs-${slug}`}><Link to={`/p/${slug}?tab=docs`}>{short(p.name)}</Link></h2>
            <Link className="btn ghost small" to={`/p/${slug}?tab=docs`}>All docs</Link>
          </div>
          <div className="list">
            {p.guides.map((g) => (
              <div key={g.slug} className="item">
                <div>
                  <Link className="cell-main" to={`/p/${slug}?tab=docs&page=${g.slug}`} dir={g.language === "ar" ? "rtl" : "ltr"}>{g.title}</Link> <span className="pill positive">User guide</span> <span className="pill">{g.language === "ar" ? "العربية" : "English"}</span>
                  <div className="cell-sub">last edited by {g.updatedBy ?? "—"} {relative(g.updatedAt)}</div>
                </div>
              </div>
            ))}
            {p.links.map((l) => (
              <div key={l.id} className="item">
                <div>
                  <a className="cell-main" href={l.url} target="_blank" rel="noopener noreferrer" dir={l.language === "ar" ? "rtl" : "ltr"}>{l.title}</a> <span className="pill">{l.kind}</span>
                  {l.notes && <div className="cell-sub">{l.notes}</div>}
                </div>
                {l.embedUrl && <button className="btn ghost small" onClick={() => setPlay(play === l.id ? null : l.id)}>{play === l.id ? "Close" : "Play"}</button>}
                {play === l.id && <div style={{ gridColumn: "1 / -1", maxWidth: 720 }}><LinkEmbed link={l} /></div>}
              </div>
            ))}
            {p.files.map((f) => (
              <div key={f.id} className="item">
                <div>
                  <span className="cell-main" dir={f.language === "ar" ? "rtl" : "ltr"}>{f.title}</span> <span className="pill">{f.kind}</span>
                  <div className="cell-sub">{f.filename} · {bytes(f.sizeBytes)}</div>
                </div>
                {f.kind === "video" ? <button className="btn ghost small" onClick={() => setPlay(play === f.id ? null : f.id)}>{play === f.id ? "Close" : "Play"}</button> : <OpenFile slug={slug} file={f} />}
                {play === f.id && <div style={{ gridColumn: "1 / -1", maxWidth: 720 }}><FileVideo slug={slug} file={f} /></div>}
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
